import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, timeout } from 'rxjs/operators';
import { Deferred } from 'src/app/common/deferred/deferred';

import { readAccountId } from '../../shared/services/account/account-validation';
import { AccountService } from '../../shared/services/account.service';
import { ConnectionStateService } from '../../shared/services/connection-state.service';
import { SessionVaultService } from '../../shared/services/session-vault/session-vault.service';
import { AuthClient, SignInResult } from '../auth-client';
import { ITokenSet } from '../token-set';
import { BackendSocketAuthenticationService } from './backend-socket-authentication.service';
import { describeError, formatRemaining, logSessionEvent, logStep, logTransition } from './session-log';
import { ACTIVE_STATES, SessionState } from './session-state-types';
import { windowLocation } from '@common/browser/window-location';

/*
 * =====================================================================================================
 * DIE SESSION IM ÜBERBLICK (Deutsch; English version below)
 * =====================================================================================================
 *
 * Diese Klasse entscheidet, in welchem Zustand die Session ist, und alles andere richtet sich danach:
 * die Route-Guards, die Login-Seite, das Status-Banner, der `SyncTrigger` und damit jeder Sync-Lauf.
 * Hier steht, welche Konstellationen es gibt, wie sie behandelt werden und was der Nutzer davon sieht.
 *
 * -----------------------------------------------------------------------------------------------------
 * 1. Die fünf Zustände
 * -----------------------------------------------------------------------------------------------------
 *
 * | Zustand     | Bedeutung                                                  | App / Nutzer                                   | Sync-Lauf                          |
 * |-------------|------------------------------------------------------------|------------------------------------------------|------------------------------------|
 * | `starting`  | Prozessstart, Token und Account-Cache noch nicht gelesen   | Guards warten (`ready`), nichts sichtbar       | keiner                             |
 * | `loggedOut` | kein brauchbarer Token, Grant abgelehnt oder Logout        | Login-Seite (einziger Zustand, der dahin führt) | laufende Arbeit abgebrochen, keiner |
 * | `online`    | Token gültig, Socket damit authentifiziert                 | App normal                                     | synchronisierter Lauf (isOnline)   |
 * | `offline`   | Account vorhanden, Socket weg oder Backend in Wartung      | App mit lokalen Daten; Web: Banner, Mobil: eigene Offline-Hinweise; Wartung: Snack-Bar | Abbruch, dann lokaler Lauf |
 * | `degraded`  | Socket da, Backend/IdP gerade nicht nutzbar, Retries laufen | App mit lokalen Daten; Banner „Verbindung zum Server wird wiederhergestellt …" | Abbruch, dann lokaler Lauf |
 *
 * `online`, `offline` und `degraded` sind die aktiven Zustände: mit einem Account lässt der Guard den
 * Nutzer in die App (`isAuthenticated`). Arbeit, die gerade läuft (Evaluation, Refresh, Logout), ist
 * kein Zustand, sondern ein Single-Flight-Promise; ein zweiter Aufruf hängt sich an den laufenden.
 *
 * -----------------------------------------------------------------------------------------------------
 * 2. Was eine Evaluation auslöst
 * -----------------------------------------------------------------------------------------------------
 *
 * - Socket verbunden (auch jeder Reconnect) → Evaluation. Ausnahme: `loggedOut` ohne Token bleibt.
 * - Socket weg oder Backend meldet Wartung → Refresh- und Retry-Timer stoppen; aus `online`/`degraded`
 *   wird `offline`, aus `starting` heraus wird evaluiert (Offline-Pfad).
 * - App kommt aus dem Hintergrund (Resume, Tab sichtbar) → in `degraded` sofortige Evaluation; in
 *   `online` nur, wenn der Refresh fällig ist oder der Token nicht mehr brauchbar ist.
 * - Refresh-Timer fällig (nur in `online`) → Evaluation, die den Token erneuert und dem Socket gibt.
 * - Backend lehnt den Token auf einem Daten-Call mit 401 ab (nur in `online`) → Evaluation.
 * - Sign-in abgeschlossen (Login-Seite, oder in einem neuen Prozess nach Browser-Roundtrip) → Evaluation.
 * - Retry-Timer fällig (in `degraded`, oder `loggedOut` ohne Account bei behaltenem Token) → Evaluation.
 *
 * -----------------------------------------------------------------------------------------------------
 * 3. Wie eine Evaluation entscheidet
 * -----------------------------------------------------------------------------------------------------
 *
 * Zuerst wird der Token aus dem Speicher gelesen (`AuthClient.initialize`). Dann:
 *
 * Offline-Pfad (Socket weg oder Wartung):
 *   Account im Speicher oder Cache → `offline`. Kein Account → `loggedOut` (liegt ein Token vor, ist der
 *   Account-Cache verloren gegangen: Sentry-Issue).
 *
 * Online-Pfad — der Token (`ensureFreshToken`):
 *   - brauchbar (Restlaufzeit über der Refresh-Marge) → weiter zum Socket.
 *   - fast abgelaufen → einmaliger Refresh beim IdP (Single-Flight, ein Refresh-Token wird nie doppelt
 *     ausgegeben). Erfolg → weiter zum Socket.
 *   - Refresh nicht möglich (IdP nicht erreichbar, Timeout, 5xx) → Übergangsstörung, siehe unten.
 *   - Refresh abgelehnt (invalid_grant, kein Refresh-Token) → Session ist vorbei: Token wird gelöscht,
 *     IdP-Session beendet, App startet neu auf der Login-Seite. Auch beim Start. Sentry-Issue.
 *   - kein Token → `loggedOut` (beim Start nur der Zustand, später voller Logout mit Neustart).
 *   - Speicher antwortet nicht (Keychain gesperrt/langsam) → mit Account `degraded` und bis zu drei
 *     erneute Leseversuche (~1 Minute), danach Login-Seite mit Sentry-Issue; ohne Account sofort
 *     Login-Seite, ein Reconnect liest den Speicher erneut.
 *
 * Online-Pfad — der Socket (`BackendSocketAuthenticationService.authenticate`, intern zwei Retries bei
 * Timeout/408/502–504):
 *   - ok → Account vom Backend übernommen, Refresh-Timer gestellt, Retry-Zähler zurück → `online`.
 *     Liefert das Backend einen anderen Account als den geladenen, endet die Session (Datenbanken sind
 *     pro Account geöffnet); Neustart.
 *   - abgelehnt (401) → genau ein erzwungener Refresh und zweiter Versuch. Zweite Ablehnung oder
 *     abgelehnter Refresh → Session vorbei wie oben. Refresh nicht möglich → Übergangsstörung.
 *   - nicht erreichbar (Retries erschöpft, 5xx, Socket zwischendurch weg) → Übergangsstörung.
 *
 * Übergangsstörung (`degradeOrWaitForBackend`):
 *   Mit Account → `degraded` und Retry-Timer. Der Nutzer arbeitet weiter, lokale Daten und die
 *   Command-Queue bleiben. Ohne Account (direkt nach dem Sign-in, oder Cache verloren) → `loggedOut`
 *   mit behaltenem Token und demselben Retry-Timer: Der Nutzer sieht die Login-Seite mit dem Hinweis,
 *   dass der Server gerade nicht erreichbar ist; antwortet das Backend, wird die Session `online` und
 *   die Login-Seite verschwindet von selbst. Ein Backend-Schluckauf kostet nie den Token.
 *
 * Verworfene Ergebnisse: Passiert während eines `await` ein Logout oder wechselt die Konnektivität,
 * wird das Ergebnis der Evaluation verworfen (`evaluationOutdated`); bei Konnektivitätswechsel läuft
 * sie einmal neu auf dem jetzt passenden Pfad. So wird nie ein `online` oder `degraded` gesetzt,
 * während der Socket schon weg ist, und kein Retry-Timer für eine beendete Session gestellt.
 *
 * -----------------------------------------------------------------------------------------------------
 * 4. Timer und Retries
 * -----------------------------------------------------------------------------------------------------
 *
 * Refresh-Timer: feuert bei Ablauf minus Marge (10 Minuten, höchstens die halbe Lebensdauer; nie früher
 * als 30 s nach dem Stellen). Gestellt nach jeder erfolgreichen Socket-Authentifizierung, gestoppt bei
 * Socket-Verlust und Logout. Hielt der Browser oder das OS den Timer im Hintergrund zurück, holt der
 * Resume-Handler ihn nach (`refreshDue`).
 *
 * Retry-Timer: Backoff 5 s, 15 s, 45 s, 120 s, dann alle 300 s. Läuft in `degraded` und in `loggedOut`
 * ohne Account bei behaltenem Token. Zähler zurück bei `online` und nach einem Sign-in; gestoppt bei
 * Socket-Verlust (der Reconnect evaluiert ohnehin), bei Resume in `degraded` (sofortige Evaluation
 * statt Warten) und bei Logout. Begrenzt ist nur der Fall „Speicher antwortet nicht" (drei Versuche).
 *
 * Nicht retried wird, was terminal ist: abgelehnter Grant, fehlender Token, zweite 401 des Backends,
 * Account-Wechsel, Logout des Nutzers. Das endet immer auf der Login-Seite.
 *
 * -----------------------------------------------------------------------------------------------------
 * 5. Was das für Sync-Läufe und Daten-Calls bedeutet
 * -----------------------------------------------------------------------------------------------------
 *
 * Der `SyncTrigger` hört nur auf `state$`: `online` startet einen synchronisierten Lauf; `offline` und
 * `degraded` brechen laufende Arbeit ab und starten einen lokalen Lauf (Datenbanken öffnen, Command-Queue
 * bleibt); `loggedOut` bricht nur ab. Bleibt der Zustand gleich (z. B. Refresh in `online`), gibt es
 * keinen neuen Lauf. Der manuelle Sync auf dem Dashboard läuft nur in aktiven Zuständen und nimmt
 * `isOnline` aus der Session. Nach einem Reconnect hält der Feathers-Hook jeden Daten-Call zurück, bis
 * der Socket authentifiziert ist — auch die, die socket.io offline gepuffert hat. Scheitert die
 * Authentifizierung, schlagen die wartenden Calls sofort mit dem Fehler fehl; eine 401 auf einem
 * Daten-Call in `online` stößt die Evaluation an (erzwungener Refresh, erneute Authentifizierung).
 * Aufrufer außerhalb von Feathers (KIM, Video-Chat) holen sich den Token über `getAccessToken()`.
 *
 * -----------------------------------------------------------------------------------------------------
 * 6. Typische Konstellationen
 * -----------------------------------------------------------------------------------------------------
 *
 * | Konstellation                                                | Ergebnis                                                | Nutzer sieht                                    | Sync                       |
 * |--------------------------------------------------------------|---------------------------------------------------------|-------------------------------------------------|----------------------------|
 * | Kaltstart, Backend da, Token gültig                          | `starting → online`                                     | App                                             | synchronisierter Lauf      |
 * | Kaltstart, Token fast abgelaufen, IdP da                     | Refresh → `online`                                      | App                                             | synchronisierter Lauf      |
 * | Kaltstart, Token abgelaufen, IdP nicht erreichbar, Account   | `degraded`, Retries                                     | App + Banner                                    | lokaler Lauf, später sync  |
 * | Kaltstart ohne Socket, Account im Cache                      | `offline`; Socket kommt → `online`                      | App, Web-Banner „Keine Verbindung zum Server."  | lokal, dann sync           |
 * | Kaltstart ohne Socket, kein Account                          | `loggedOut`                                             | Login-Seite                                     | keiner                     |
 * | Kaltstart, kein Token                                        | `loggedOut`                                             | Login-Seite                                     | keiner                     |
 * | Kaltstart, Refresh abgelehnt                                 | Logout mit Neustart                                     | Login-Seite (IdP-Session beendet)               | keiner                     |
 * | Kaltstart, Keychain antwortet nicht, Account im Cache        | `degraded`, 3 Leseversuche, dann Logout                 | App + Banner, ggf. Login-Seite                  | lokal                      |
 * | Online, Socket fällt weg                                     | `offline`; Reconnect → `online`                         | Web-Banner / Mobil-Hinweise                     | Abbruch, lokal, dann sync  |
 * | Online, Wartungsfenster                                      | `offline`; Ende → `online`                              | Wartungs-Snack-Bar mit Countdown                | Abbruch, lokal, dann sync  |
 * | Online, Refresh fällig, IdP da                               | `online` (unverändert)                                  | nichts                                          | kein neuer Lauf            |
 * | Online, Refresh fällig, IdP nicht erreichbar                 | `degraded`, Retries                                     | Banner                                          | Abbruch, lokal             |
 * | Online, Refresh abgelehnt                                    | Logout mit Neustart                                     | Login-Seite                                     | Abbruch                    |
 * | Online, Backend 401 auf Daten-Call                           | erzwungener Refresh, Re-Auth → `online`; zweite 401 → Logout | nichts / Login-Seite                       | – / Abbruch                |
 * | Online, Backend 503/Timeout bei Re-Auth                      | `degraded`, Retries; Backend zurück → `online`          | Banner                                          | Abbruch, lokal, dann sync  |
 * | Sign-in ok, Backend nicht erreichbar (kein Account)          | `loggedOut` mit Token, Retries → `online`               | Login-Seite mit Hinweis, geht von selbst weiter  | keiner, dann sync          |
 * | Sign-in mobil, Socket vom Browser-Roundtrip getrennt         | bis 10 s warten, Reconnect etabliert die Session        | Login-Seite, dann App                           | sync                       |
 * | Resume in `degraded`                                         | sofortige Evaluation                                    | Banner verschwindet bei Erfolg                  | sync bei `online`          |
 * | Backend liefert anderen Account als geladen                  | Logout mit Neustart                                     | Login-Seite                                     | Abbruch                    |
 * | Nutzer-Logout / Sperrbildschirm                              | `loggedOut`, Backend-Logout, Token/Account/Vault weg, Neustart | Login-Seite                              | Abbruch                    |
 *
 * Logout im Detail (`runLogout`): Jeder Schritt ist einzeln abgesichert, damit ein fehlschlagender
 * Speicher die anderen nicht verhindert — Backend-Logout (nur bei verbundenem Socket), Token
 * vergessen, Account vergessen, Vault leeren (mobil). Danach immer: `loggedOut`, Service-Worker
 * abmelden (Web), IdP-Session beenden (bei Socket), Neustart des Prozesses (mobil, oder Web offline;
 * Web online verlässt die Seite ohnehin zum IdP). Arbeit, die vor dem Logout gestartet wurde, schreibt
 * nicht mehr in die Session (`_generation`).
 *
 * -----------------------------------------------------------------------------------------------------
 * 7. Monitoring
 * -----------------------------------------------------------------------------------------------------
 *
 * Alles Diagnostische läuft über `session-log.ts`: jeder Schritt und Übergang ist eine Konsolenzeile,
 * die Sentry als Breadcrumb aufnimmt; die letzten 30 Zeilen hängen als Kontext `session` an jedem
 * Sentry-Event.
 * Die `SessionTelemetry` setzt die Tags `session.state`, `connectivity` und `token.state` und den
 * Nutzer. Sentry-Events gibt es nur für gezählte Ergebnisse, jeweils mit Code und Ursache als Tags und
 * Fingerprint (`logSessionEvent`): `forced_logout` (warning; rejected_grant, no_token, storage_silent),
 * `session_anomaly` (error; no_account_with_token, account_changed, evaluation_failed,
 * token_persist_failed), `logout_step_failed`, `sign_in_failed` (Code des IdP), `token_migration`
 * (info) / `token_migration_failed`, `degraded_long` (5 min), `stuck_starting` (15 s),
 * `unauthenticated_call`. Erwartete Ausgänge — ein abgelehnter Grant nach langer Inaktivität, ein vom
 * Nutzer abgebrochener Login, ein regulärer Start ohne Token — sind Warnungen oder Breadcrumbs, nie
 * Fehler.
 *
 * =====================================================================================================
 * THE SESSION AT A GLANCE (English)
 * =====================================================================================================
 *
 * This class decides which state the session is in, and everything else follows from it: the route
 * guards, the login page, the status banner, the `SyncTrigger` and with it every sync run. This is the
 * map of the constellations, how each is handled, and what the user sees.
 *
 * -----------------------------------------------------------------------------------------------------
 * 1. The five states
 * -----------------------------------------------------------------------------------------------------
 *
 * | state       | meaning                                                     | app / user                                     | sync run                           |
 * |-------------|-------------------------------------------------------------|------------------------------------------------|------------------------------------|
 * | `starting`  | process start, token and account cache not read yet         | guards wait (`ready`), nothing visible         | none                               |
 * | `loggedOut` | no usable token, grant rejected, or logout                  | login page (the only state that leads there)   | running work cancelled, no run     |
 * | `online`    | token valid, socket authenticated with it                   | app as usual                                   | synced run (isOnline)              |
 * | `offline`   | account present, socket down or backend in maintenance      | app on local data; web: banner, mobile: its own offline hints; maintenance: snack bar | cancel, then local run |
 * | `degraded`  | socket up, backend/IdP not usable right now, retries running | app on local data; banner "reconnecting to the server" | cancel, then local run     |
 *
 * `online`, `offline` and `degraded` are the active states: with an account the guard lets the user
 * into the app (`isAuthenticated`). Work in flight (evaluation, refresh, logout) is never a state but
 * a single-flight promise; a second caller joins the running one.
 *
 * -----------------------------------------------------------------------------------------------------
 * 2. What triggers an evaluation
 * -----------------------------------------------------------------------------------------------------
 *
 * - Socket connected (every reconnect too) → evaluation. Exception: `loggedOut` without a token stays.
 * - Socket lost or backend in maintenance → refresh and retry timers stop; `online`/`degraded` become
 *   `offline`; from `starting` an evaluation runs (offline path).
 * - App resumed (platform resume, tab visible) → in `degraded` an immediate evaluation; in `online`
 *   only when the refresh is due or the token is no longer usable.
 * - Refresh timer due (only in `online`) → evaluation that renews the token and hands it to the socket.
 * - Backend answers a data call with 401 (only in `online`) → evaluation.
 * - Sign-in completed (login page, or in a fresh process after the browser round trip) → evaluation.
 * - Retry timer due (in `degraded`, or `loggedOut` without account with the token kept) → evaluation.
 *
 * -----------------------------------------------------------------------------------------------------
 * 3. How an evaluation decides
 * -----------------------------------------------------------------------------------------------------
 *
 * First the token is read from storage (`AuthClient.initialize`). Then:
 *
 * Offline path (socket down or maintenance):
 *   Account in memory or cache → `offline`. No account → `loggedOut` (with a token present the account
 *   cache was lost: Sentry issue).
 *
 * Online path — the token (`ensureFreshToken`):
 *   - usable (more than the refresh margin left) → on to the socket.
 *   - about to expire → one refresh at the IdP (single-flight, a refresh token is never spent twice).
 *     Success → on to the socket.
 *   - refresh not possible (IdP unreachable, timeout, 5xx) → transient trouble, see below.
 *   - refresh rejected (invalid_grant, no refresh token) → the session is over: token dropped, IdP
 *     session ended, app restarts on the login page. Even at start. Sentry issue.
 *   - no token → `loggedOut` (at start just the state, later a full logout with restart).
 *   - storage does not answer (keychain locked/slow) → with an account `degraded` and up to three
 *     re-reads (~1 minute), then login page with a Sentry issue; without an account the login page at
 *     once, a reconnect reads the storage again.
 *
 * Online path — the socket (`BackendSocketAuthenticationService.authenticate`, two internal retries on
 * timeout/408/502–504):
 *   - ok → backend account applied, refresh timer armed, retry counter reset → `online`. If the backend
 *     returns a different account than the loaded one the session ends (databases are opened per
 *     account); restart.
 *   - refused (401) → exactly one forced refresh and a second attempt. Second refusal or rejected
 *     refresh → session over as above. Refresh not possible → transient trouble.
 *   - unavailable (retries exhausted, 5xx, socket gone meanwhile) → transient trouble.
 *
 * Transient trouble (`degradeOrWaitForBackend`):
 *   With an account → `degraded` and a retry timer. The user keeps working, local data and the command
 *   queue stay. Without an account (right after sign-in, or cache lost) → `loggedOut` with the token
 *   kept and the same retry timer: the user sees the login page with a hint that the server is not
 *   reachable; when the backend answers the session goes `online` and the login page leaves by itself.
 *   A backend hiccup never costs the token.
 *
 * Discarded results: if a logout happens or connectivity flips during an `await`, the evaluation's
 * result is discarded (`evaluationOutdated`); on a connectivity flip it runs once more on the path
 * that now applies. So `online` or `degraded` is never set while the socket is already gone, and no
 * retry timer is armed for a session that has ended.
 *
 * -----------------------------------------------------------------------------------------------------
 * 4. Timers and retries
 * -----------------------------------------------------------------------------------------------------
 *
 * Refresh timer: fires at expiry minus the margin (10 minutes, at most half the lifetime; never sooner
 * than 30 s after arming). Armed after every successful socket authentication, cancelled on socket
 * loss and logout. If the browser or the OS held the timer back in the background, the resume handler
 * catches up (`refreshDue`).
 *
 * Retry timer: backoff 5 s, 15 s, 45 s, 120 s, then every 300 s. Runs in `degraded` and in `loggedOut`
 * without an account with the token kept. Counter reset on `online` and after a sign-in; cancelled on
 * socket loss (the reconnect evaluates anyway), on resume in `degraded` (evaluate now instead of
 * waiting) and on logout. Only the "storage does not answer" case is bounded (three attempts).
 *
 * Never retried: what is terminal — rejected grant, missing token, second 401 from the backend, account
 * change, the user's logout. That always ends on the login page.
 *
 * -----------------------------------------------------------------------------------------------------
 * 5. What this means for sync runs and data calls
 * -----------------------------------------------------------------------------------------------------
 *
 * The `SyncTrigger` listens to `state$` only: `online` starts a synced run; `offline` and `degraded`
 * cancel running work and start a local run (databases open, command queue stays); `loggedOut` only
 * cancels. An unchanged state (e.g. a refresh in `online`) starts no new run. The manual sync on the
 * dashboard runs only in active states and takes `isOnline` from the session. After a reconnect the
 * Feathers hook holds every data call until the socket is authenticated — including those socket.io
 * buffered while offline. If the authentication fails, the waiting calls fail at once with that error;
 * a 401 on a data call in `online` triggers the evaluation (forced refresh, re-authentication).
 * Callers outside Feathers (KIM, video chat) take the token from `getAccessToken()`.
 *
 * -----------------------------------------------------------------------------------------------------
 * 6. Typical constellations
 * -----------------------------------------------------------------------------------------------------
 *
 * | constellation                                               | outcome                                                  | user sees                                       | sync                       |
 * |-------------------------------------------------------------|----------------------------------------------------------|-------------------------------------------------|----------------------------|
 * | cold start, backend up, token valid                         | `starting → online`                                      | app                                             | synced run                 |
 * | cold start, token about to expire, IdP up                   | refresh → `online`                                       | app                                             | synced run                 |
 * | cold start, token expired, IdP unreachable, account cached  | `degraded`, retries                                      | app + banner                                    | local run, synced later    |
 * | cold start without socket, account cached                   | `offline`; socket comes → `online`                       | app, web banner "no connection to the server"   | local, then synced         |
 * | cold start without socket, no account                       | `loggedOut`                                              | login page                                      | none                       |
 * | cold start, no token                                        | `loggedOut`                                              | login page                                      | none                       |
 * | cold start, refresh rejected                                | logout with restart                                      | login page (IdP session ended)                  | none                       |
 * | cold start, keychain silent, account cached                 | `degraded`, 3 re-reads, then logout                      | app + banner, possibly login page               | local                      |
 * | online, socket lost                                         | `offline`; reconnect → `online`                          | web banner / mobile hints                       | cancel, local, then synced |
 * | online, maintenance window                                  | `offline`; end → `online`                                | maintenance snack bar with countdown            | cancel, local, then synced |
 * | online, refresh due, IdP up                                 | `online` (unchanged)                                     | nothing                                         | no new run                 |
 * | online, refresh due, IdP unreachable                        | `degraded`, retries                                      | banner                                          | cancel, local              |
 * | online, refresh rejected                                    | logout with restart                                      | login page                                      | cancel                     |
 * | online, backend 401 on a data call                          | forced refresh, re-auth → `online`; second 401 → logout | nothing / login page                            | – / cancel                 |
 * | online, backend 503/timeout on re-auth                      | `degraded`, retries; backend back → `online`             | banner                                          | cancel, local, then synced |
 * | sign-in ok, backend unreachable (no account)                | `loggedOut` with token, retries → `online`               | login page with hint, continues by itself       | none, then synced          |
 * | mobile sign-in, socket dropped by the browser round trip    | wait up to 10 s, reconnect establishes the session       | login page, then app                            | synced                     |
 * | resume in `degraded`                                        | immediate evaluation                                     | banner disappears on success                    | synced on `online`         |
 * | backend returns a different account than loaded            | logout with restart                                      | login page                                      | cancel                     |
 * | user logout / lock screen                                   | `loggedOut`, backend logout, token/account/vault gone, restart | login page                                | cancel                     |
 *
 * Logout in detail (`runLogout`): every step is guarded on its own so one failing store cannot stop the
 * others — backend logout (only with a connected socket), forget the token, forget the account, clear
 * the vault (mobile). Then always: `loggedOut`, unregister the service workers (web), end the IdP
 * session (with a socket), restart the process (mobile, or web offline; web online leaves the page for
 * the IdP anyway). Work started before the logout no longer writes into the session (`_generation`).
 *
 * -----------------------------------------------------------------------------------------------------
 * 7. Monitoring
 * -----------------------------------------------------------------------------------------------------
 *
 * All diagnostics go through `session-log.ts`: every step and transition is a console line that
 * Sentry keeps as a breadcrumb, and the last 30 lines travel as the `session` context on every
 * Sentry event.
 * The `SessionTelemetry` keeps the tags `session.state`, `connectivity` and `token.state` and the
 * user current. Sentry events exist only for counted outcomes, each with code and cause as tags and
 * fingerprint (`logSessionEvent`): `forced_logout` (warning; rejected_grant, no_token, storage_silent),
 * `session_anomaly` (error; no_account_with_token, account_changed, evaluation_failed,
 * token_persist_failed), `logout_step_failed`, `sign_in_failed` (the IdP's code), `token_migration`
 * (info) / `token_migration_failed`, `degraded_long` (5 min), `stuck_starting` (15 s),
 * `unauthenticated_call`. Expected outcomes — a rejected grant after long inactivity, a sign-in the
 * user cancelled, a regular start without a token — are warnings or breadcrumbs, never errors.
 */

/**
 * How early before its expiry a token is renewed: 10 minutes, but never more than half of the
 * token's lifetime (see `refreshBufferFor`).
 *
 * A token counts as usable while it has at least this margin left, and the refresh is due when
 * this margin is left — one number for both, so a token is never "usable" and "due" at once. The
 * production access token lives for a day, so this is a wide margin for a slow network or an app
 * that was paused around the moment the timer would have fired. Test environments hand out
 * tokens of a few minutes; for those the margin shrinks to half the lifetime, otherwise every
 * token would be due on arrival and the refresh would loop without pause.
 */
export const EXPIRY_BUFFER_MS = 10 * 60 * 1000;

/** A refresh timer never fires sooner than this, whatever the token says. */
const MIN_REFRESH_DELAY_MS = 30 * 1000;

/** setTimeout cannot take more than a signed 32-bit millisecond count. */
const MAX_TIMER_MS = 2_147_483_647;

/** Backoff between retries while degraded; the last value repeats. */
const RETRY_DELAYS_MS = [5_000, 15_000, 45_000, 120_000, 300_000];

/**
 * How often a token storage that did not answer is read again before the session gives up on
 * it: three attempts, i.e. about a minute with the backoff above. The cases this is for heal
 * within seconds — a slow secure-storage plugin, or an iOS keychain that is still locked right
 * after a reboot and opens the moment the user unlocks the device. A storage that is still
 * silent after a minute will not heal by waiting, and a user parked in `degraded` forever would
 * have no way to notice that a new login is the way out; the login page at least offers one.
 */
const UNREADABLE_STORE_ATTEMPTS = 3;

/** How long a completed sign-in waits for a socket that the identity provider's browser round trip dropped. */
const SOCKET_AFTER_SIGN_IN_TIMEOUT_MS = 10_000;

/** Result of asking for a usable access token. */
type TokenCheck =
  | { kind: 'ok'; accessToken: string; expiresAt: number; refreshAt: number }
  /** transient: identity provider unreachable, timeout, 5xx — the stored token is still worth keeping */
  | { kind: 'unavailable'; reason: string }
  /** terminal: the authorization server rejected the grant, the session is over */
  | { kind: 'rejected'; reason: string }
  /** no token: nothing is stored (or the user logged out) */
  | { kind: 'none' }
  /**
   * The token storage did not answer, so we do not know whether a token exists. Not the same as
   * `none`: a valid token may well sit in the keychain (#26615), and treating this as "logged
   * out" would cost the user a login they did not need.
   */
  | { kind: 'unreadable' };

type TokenOk = Extract<TokenCheck, { kind: 'ok' }>;

/**
 * The session state machine (#26615): decides when the token is refreshed, when the socket is
 * (re-)authenticated, and what a failure means. Everything else — the pipeline, the guards, the
 * login page, the pages waiting for a login — reads `state$` / `isAuthenticated$` and asks this
 * class for actions.
 *
 * Events arrive as plain method calls (connectivity, resume, refresh due, sign-in, logout,
 * backend refusal). The only subscriptions live in the constructor: the connectivity state, the
 * platform resume, page visibility and the backend's refusal signal. Work in flight is a
 * single-flight promise, never a state: an evaluation, a refresh, a logout.
 *
 * Rule: `loggedOut` is the only state that leads to /login, and only a missing token, a rejected
 * grant or the user's own logout ends the session for good. Everything transient ends in
 * `degraded` with a retry schedule — the user keeps the app, the local data and the command
 * queue. Without an account to keep the app open with, the same transient trouble waits on the
 * login page instead (`loggedOut`, token kept, same retry schedule).
 */
@Injectable({ providedIn: 'root' })
export class SessionController {
  private readonly _state$ = new BehaviorSubject<SessionState>('starting');
  private readonly _ready = new Deferred<void>();
  private _evaluation: Promise<SessionState> | null = null;
  private _refresh: Promise<TokenCheck> | null = null;
  private _logout: Promise<void> | null = null;
  private _refreshHandle: ReturnType<typeof setTimeout> | null = null;
  private _refreshDueAt: number | null = null;
  private _retryHandle: ReturnType<typeof setTimeout> | null = null;
  private _retryAttempt = 0;
  /** Bumped by every logout; work that started before must not write into the session afterwards. */
  private _generation = 0;
  /** Whether the running evaluation took the online or the offline path. */
  private _evaluatedOnline = false;
  /** The generation the running evaluation started in; see `evaluationOutdated`. */
  private _evaluationGeneration = 0;

  public readonly state$: Observable<SessionState> = this._state$.asObservable();
  /** `isAuthenticated` as a stream: replays the current value, emits on change only. */
  public readonly isAuthenticated$: Observable<boolean>;

  constructor(
    private readonly _oauth: AuthClient,
    private readonly _backend: BackendSocketAuthenticationService,
    private readonly _accountService: AccountService,
    private readonly _connectivity: ConnectionStateService,
    private readonly _platform: Platform,
    private readonly _sessionVault: SessionVaultService
  ) {
    this.isAuthenticated$ = combineLatest([this.state$, this._accountService.account$]).pipe(
      map(([state, account]) => ACTIVE_STATES.has(state) && account != null),
      distinctUntilChanged()
    );
    this._connectivity.isOnline$.subscribe(online => void this.onConnectivity(online));
    this._platform.resume.subscribe(() => void this.onAppResumed());
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          void this.onAppResumed();
        }
      });
    }
    this._backend.refused$.subscribe(() => void this.onBackendRefusedToken());
    this._oauth.unsolicitedSignIn$.subscribe(() => void this.onUnsolicitedSignIn());
  }

  //#region state

  public get state(): SessionState {
    return this._state$.value;
  }

  /** Resolves once `starting` has been left; the route guards wait for it. */
  public get ready(): Promise<void> {
    return this._ready.promise;
  }

  /** An active session with an account: the one rule for the route guards, the navigator and the pages. */
  public get isAuthenticated(): boolean {
    return ACTIVE_STATES.has(this.state) && this._accountService.hasAccount;
  }

  /** Resolves once the user is authenticated — immediately when they already are. */
  public whenAuthenticated(): Promise<void> {
    return firstValueFrom(this.isAuthenticated$.pipe(filter(Boolean))).then(() => undefined);
  }

  //#endregion

  //#region actions

  /** A usable access token for callers outside Feathers (KIM, video chat); null when none can be had right now. */
  public async getAccessToken(): Promise<string | null> {
    await this._oauth.initialize();
    const token = await this.ensureFreshToken();
    if (token.kind === 'rejected') {
      // the same answer the scheduled refresh gives: the session is over, whoever asked
      this.endSessionAfterRejectedGrant(token.reason);
      return null;
    }
    return token.kind === 'ok' ? token.accessToken : null;
  }

  /**
   * Signs the user in. Mobile: settles when the identity provider's callback arrived and the
   * session was established. Web: the page navigates away; the result arrives in the next process.
   */
  public async signIn(email: string, returnUrl: string | null = null): Promise<SignInResult> {
    logStep('sign-in', 'started');
    const result = await this._oauth.login(await this.signInExtras(email), returnUrl);
    logStep('sign-in', result.kind);
    if (result.kind === 'signedIn') {
      this.resetRetries();
      await this.awaitSocketAfterSignIn();
      if (!ACTIVE_STATES.has(this.state)) {
        await this.evaluate('sign-in completed');
      }
    }
    return result;
  }

  /** Where the user wanted to go before the sign-in; `null` when nowhere in particular. Consumed on read. */
  public consumePostLoginTarget(): string | null {
    return this._oauth.consumePostLoginTarget();
  }

  /**
   * Ends the session: backend, account, vault, then the identity provider. The state turns
   * `loggedOut` before the identity provider round trip so the navigator can act at once.
   * Single-flight: a second call while one runs joins it.
   */
  public logout(options: { reason: string; skipReload?: boolean }): Promise<void> {
    if (!this._logout) {
      this._logout = this.runLogout(options).finally(() => {
        this._logout = null;
      });
    }
    return this._logout;
  }

  private async runLogout(options: { reason: string; skipReload?: boolean }): Promise<void> {
    logStep('logout', options.reason);
    // an evaluation or refresh still in flight belongs to the session that is ending
    this._generation++;
    this.cancelRefresh();
    this.cancelRetry();

    const socketConnected = this._connectivity.isSocketConnected;
    const hybrid = this._platform.is('hybrid');
    try {
      // every step on its own: one failing store (IndexedDB on iOS, #26615) must not keep the
      // others from running, and none of them may keep the logout from finishing
      if (socketConnected) {
        await this.logoutStep('backend', () => this._backend.logout());
      }
      await this.logoutStep('token', () => this._oauth.clear());
      await this.logoutStep('account', () => this._accountService.clear());
      if (hybrid && this._sessionVault.vaultInitialized) {
        await this.logoutStep('vault', () => this._sessionVault.clearVaultAndResetLockMode());
      }
    } finally {
      // whatever happened above, this process ends logged out and restarts
      this.transition('loggedOut', `logout: ${options.reason}`);
      if (!hybrid) {
        // the push service worker would keep delivering to a signed-out browser
        await unregisterServiceWorkers();
      }
      if (socketConnected) {
        // the web navigates away to the identity provider; mobile settles with its callback
        await this.logoutStep('identity_provider', () => this._oauth.endSession());
      }
      // The process restarts so no per-user state can leak into the next session. The web online
      // case already left the page for the identity provider.
      if (!options.skipReload && (hybrid || !socketConnected)) {
        windowLocation.reload();
      }
    }
  }

  /** One cleanup step of the logout: a failure is reported and does not stop the others. */
  private async logoutStep(step: string, action: () => Promise<void>): Promise<void> {
    try {
      await action();
    } catch (error) {
      logSessionEvent('logout_step_failed', step, `${step} failed: ${describeError(error)}`, error);
    }
  }

  //#endregion

  //#region sign-in details

  /** Mobile: the identity provider gets the e-mail as a hint, and a fresh login is forced when it is not the last user's. */
  private async signInExtras(email: string): Promise<Record<string, string>> {
    const extras: Record<string, string> = {};
    if (!this._platform.is('hybrid')) {
      return extras;
    }
    const normalizedEmail = email?.trim().toLowerCase();
    if (normalizedEmail) {
      extras.login_hint = normalizedEmail;
    }
    const lastEmail = await this._accountService.cachedEmail();
    if (!lastEmail || lastEmail !== normalizedEmail) {
      extras.prompt = 'login';
    }
    return extras;
  }

  /**
   * The system browser used for the sign-in pauses the app; socket.io may have dropped the
   * connection meanwhile and reconnects on its own within seconds. Evaluating before that would
   * find no backend and, without a cached account, end in `loggedOut` despite the fresh token.
   * A socket that comes back during the wait establishes the session through `onConnectivity`.
   */
  private async awaitSocketAfterSignIn(): Promise<void> {
    if (this._connectivity.isOnline) {
      return;
    }
    logStep('sign-in', `socket not connected, waiting up to ${SOCKET_AFTER_SIGN_IN_TIMEOUT_MS / 1000}s for it`);
    const online = await firstValueFrom(
      this._connectivity.isOnline$.pipe(
        filter(Boolean),
        timeout(SOCKET_AFTER_SIGN_IN_TIMEOUT_MS),
        catchError(() => of(false))
      )
    );
    logStep('sign-in', online ? 'socket connected' : 'socket still not connected, the session follows the socket');
  }

  //#endregion

  //#region events

  /** `isOnline$` is silent until the socket state is known, so this never runs on a guess. */
  private async onConnectivity(online: boolean): Promise<void> {
    if (online) {
      // No token and none expected: nothing to evaluate. When the storage never answered we do
      // not know that, so the reconnect gets another chance to read it.
      if (this.state === 'loggedOut' && !this._oauth.currentToken && this._oauth.tokenState !== 'unknown') {
        logStep('connectivity', 'online, but no token: staying logged out');
        return;
      }
      await this.evaluate('socket connected');
      return;
    }

    this.cancelRefresh();
    this.cancelRetry();
    const reason = this._connectivity.isSocketConnected ? 'backend in maintenance' : 'socket disconnected';
    if (this.state === 'starting') {
      await this.evaluate(reason);
      return;
    }
    if (this.state === 'online' || this.state === 'degraded') {
      this.transition('offline', reason);
    }
  }

  private async onAppResumed(): Promise<void> {
    if (this.state === 'degraded') {
      this.cancelRetry();
      await this.evaluate('app resumed while degraded');
      return;
    }
    if (this.state === 'online' && (this.refreshDue || !this.tokenUsable)) {
      await this.onRefreshDue();
    }
  }

  /**
   * A due refresh is an ordinary evaluation: `establishOnline()` refreshes the token and hands it
   * to the socket. Going through `evaluate()` keeps it single-flight, so a second resume event, a
   * reconnect or a backend refusal that arrives at the same moment joins this run instead of
   * authenticating the socket a second time.
   */
  private async onRefreshDue(): Promise<void> {
    if (this.state !== 'online') {
      logStep('refresh due', `ignored in state ${this.state}`);
      return;
    }
    logStep('refresh due', `exp in ${this.describeTokenLifetime()}`);
    await this.evaluate('refresh due');
  }

  /** A sign-in completed in a fresh process (the OS killed the app under the system browser). */
  private async onUnsolicitedSignIn(): Promise<void> {
    if (ACTIVE_STATES.has(this.state)) {
      return;
    }
    logStep('sign-in', 'completed in a new process');
    await this.evaluate('sign-in completed in a new process');
  }

  private async onBackendRefusedToken(): Promise<void> {
    if (this.state !== 'online') {
      // a data call that ran before the socket was authenticated; the state machine handles the
      // connection itself, so this is only counted
      logSessionEvent('unauthenticated_call', this.state, 'a data call got 401 while the session was not online');
      return;
    }
    if (this._evaluation) {
      return;
    }
    logStep('backend', 'refused the connection token on a data call');
    await this.evaluate('backend refused the token');
  }

  //#endregion

  //#region evaluation

  /**
   * Single-flight. A connectivity change that arrives while an evaluation runs cannot join it
   * (the path, online or offline, was chosen at the start). The running evaluation notices the
   * change at its next `await` and stops (see `evaluationOutdated`); here it runs once more on the
   * path that now applies.
   */
  private evaluate(reason: string): Promise<SessionState> {
    if (this._evaluation) {
      return this._evaluation;
    }
    this._evaluation = this.runEvaluation(reason)
      .catch(error => {
        logSessionEvent('session_anomaly', 'evaluation_failed', `${reason}: ${describeError(error)}`, error);
        return this.degradeOrWaitForBackend(`${reason} | evaluation failed: ${describeError(error)}`);
      })
      .finally(() => {
        this._evaluation = null;
        this._ready.resolve();
      })
      .then(state => {
        if (state !== 'loggedOut' && this._connectivity.isOnline !== this._evaluatedOnline) {
          logStep('evaluation', 'connectivity changed meanwhile, evaluating again');
          return this.evaluate(`${reason} | connectivity changed meanwhile`);
        }
        return state;
      });
    return this._evaluation;
  }

  private async runEvaluation(reason: string): Promise<SessionState> {
    this._evaluationGeneration = this._generation;
    this._evaluatedOnline = this._connectivity.isOnline;
    await this._oauth.initialize();
    if (this.evaluationOutdated) {
      return this.state;
    }
    if (!this._evaluatedOnline) {
      return this.evaluateOffline(reason);
    }
    return this.establishOnline(reason);
  }

  /**
   * The world changed while the running evaluation was waiting on something, so whatever that wait
   * returned must not be acted on. Checked after every `await` on the evaluation path; the
   * evaluation is single-flight, so one pair of fields is enough.
   *
   * - A logout happened: the account is gone, so the logged-out branches would fire a false
   *   "forced logout" Sentry issue, call `logout()` a second time or arm a retry timer for a
   *   session that no longer exists.
   * - Connectivity flipped: a backend answer that arrives after the socket dropped would put the
   *   session `online` (or `degraded`, with a retry timer) while nothing can reach the backend.
   *   `SyncTrigger` would start a synced run on that state and cancel it a moment later, when the
   *   re-run in `evaluate()` corrects the state to `offline`. Stopping here means that state is
   *   never emitted.
   */
  private get evaluationOutdated(): boolean {
    if (this._evaluationGeneration !== this._generation) {
      logStep('evaluation', 'result discarded, the session was ended meanwhile');
      return true;
    }
    if (this._connectivity.isOnline !== this._evaluatedOnline) {
      logStep('evaluation', 'result discarded, connectivity changed meanwhile');
      return true;
    }
    return false;
  }

  private async evaluateOffline(reason: string): Promise<SessionState> {
    const account = this._accountService.current ?? (await this._accountService.loadCached());
    if (this.evaluationOutdated) {
      return this.state;
    }
    if (account) {
      return this.transition('offline', reason);
    }
    return this.loggedOutWithoutAccount(reason);
  }

  private async establishOnline(reason: string): Promise<SessionState> {
    const token = await this.ensureFreshToken();
    if (this.evaluationOutdated) {
      return this.state;
    }
    switch (token.kind) {
      case 'none':
        if (this.state !== 'starting') {
          // at start a missing token is a regular logged-out start; later it vanished under a session
          logSessionEvent('forced_logout', 'no_token', reason);
        }
        return this.leaveSession(`${reason} | no token`);
      case 'unreadable':
        return this.retryUnreadableStore(reason);
      case 'rejected':
        return this.endSessionAfterRejectedGrant(token.reason);
      case 'unavailable':
        return this.degradeOrWaitForBackend(`${reason} | token refresh unavailable: ${token.reason}`);
      default:
        return this.authenticateAndGoOnline(token, reason, true);
    }
  }

  /**
   * Hands the token to the socket. A refusal is answered with one forced refresh and a second
   * attempt; a second refusal means the backend does not accept this session at all.
   */
  private async authenticateAndGoOnline(
    token: TokenOk,
    reason: string,
    mayRefreshOnRefusal: boolean
  ): Promise<SessionState> {
    const auth = await this._backend.authenticate(token.accessToken);
    if (this.evaluationOutdated) {
      return this.state;
    }
    if (auth.kind === 'ok') {
      // The databases are opened per account and stay open for the process. Another account
      // must therefore never be applied in this process; it gets a fresh start instead.
      const loadedId = this._accountService.current?._id;
      const incomingId = readAccountId(auth.user);
      if (loadedId && incomingId && loadedId !== incomingId) {
        logSessionEvent('session_anomaly', 'account_changed', `account changed from ${loadedId} to ${incomingId}`);
        return this.leaveSession(`account changed from ${loadedId} to ${incomingId}`, { alsoAtStart: true });
      }
      await this._accountService.applyBackendUser(auth.user);
      this.scheduleRefresh(token.refreshAt);
      this.resetRetries();
      logStep('socket auth', `ok | exp in ${formatRemaining(token.expiresAt)}`);
      return this.transition('online', reason);
    }
    if (auth.kind === 'refused') {
      logStep('socket auth', `refused: ${auth.reason}`);
      if (!mayRefreshOnRefusal) {
        return this.endSessionAfterRejectedGrant(`backend refused the refreshed token: ${auth.reason}`);
      }
      const fresh = await this.ensureFreshToken({ force: true });
      if (this.evaluationOutdated) {
        return this.state;
      }
      switch (fresh.kind) {
        case 'ok':
          return this.authenticateAndGoOnline(fresh, `${reason} | retry with refreshed token`, false);
        case 'rejected':
          return this.endSessionAfterRejectedGrant(fresh.reason);
        default:
          return this.degradeOrWaitForBackend(
            `${reason} | token refused and refresh unavailable: ${describeCheck(fresh)}`
          );
      }
    }
    return this.degradeOrWaitForBackend(`${reason} | backend unavailable: ${auth.reason}`);
  }

  /** Transient trouble: keep the app with the account we have, retry in the background. */
  private async degradeOrWaitForBackend(reason: string): Promise<SessionState> {
    const account = this._accountService.current ?? (await this._accountService.loadCached());
    if (this.evaluationOutdated) {
      return this.state;
    }
    if (!account) {
      return this.waitForBackendWithoutAccount(reason);
    }
    this.scheduleRetry();
    return this.transition('degraded', reason);
  }

  /**
   * Transient trouble, but no account to keep the app open with: right after a sign-in the
   * account has never been fetched, or its cache was evicted (#26615). The token is still valid,
   * so this is not a logout — clearing it and ending the identity provider session (on a device
   * the system browser would open by itself) would cost the user a login that a backend hiccup
   * does not justify. Without an account the login page is the only place for the user; the
   * retries keep evaluating, and once the backend answers the session is `online` and the login
   * page leaves through `isAuthenticated$`. A socket reconnect does the same (`onConnectivity`).
   */
  private waitForBackendWithoutAccount(reason: string): SessionState {
    this.scheduleRetry();
    return this.transition('loggedOut', `${reason} | no account yet, token kept`);
  }

  /**
   * The token storage did not answer (see `TokenCheck.unreadable`). With a cached account the user
   * keeps the app and the session is `degraded`; each retry re-reads the storage, because
   * `AuthClient.initialize()` does not memoise a read that ended `unknown`. The retries are
   * bounded by `UNREADABLE_STORE_ATTEMPTS`; after that the session ends like a missing token
   * would, and the case is reported so we learn how often a keychain stays silent. Without an
   * account the user has to log in anyway, so the login page comes at once — a reconnect still
   * re-evaluates (see `onConnectivity`), so a late answer of the storage can rescue the session.
   */
  private async retryUnreadableStore(reason: string): Promise<SessionState> {
    const account = this._accountService.current ?? (await this._accountService.loadCached());
    if (this.evaluationOutdated) {
      return this.state;
    }
    if (account && this._retryAttempt < UNREADABLE_STORE_ATTEMPTS) {
      this.scheduleRetry();
      return this.transition('degraded', `${reason} | token storage did not answer`);
    }
    if (account) {
      logSessionEvent(
        'forced_logout',
        'storage_silent',
        `the token storage did not answer in ${UNREADABLE_STORE_ATTEMPTS} attempts, giving up`
      );
    }
    return this.leaveSession(`${reason} | token storage did not answer (tokenState=unknown)`);
  }

  /**
   * Offline and no account in memory or in the cache. With a token present the user never logged
   * out — the storage lost the account (#26615); that case is worth a Sentry issue, a regular
   * logged-out start is not.
   */
  private loggedOutWithoutAccount(reason: string): SessionState {
    const tokenState = this._oauth.tokenState;
    if (tokenState === 'token-present') {
      logSessionEvent(
        'session_anomaly',
        'no_account_with_token',
        `${reason} | the token is there, but no account could be found`
      );
    }
    return this.leaveSession(`${reason} | no account (tokenState=${tokenState})`);
  }

  private endSessionAfterRejectedGrant(reason: string): SessionState {
    // the expected end of a stale session (refresh token expired or revoked): counted, not alerted
    logSessionEvent('forced_logout', 'rejected_grant', reason);
    // even at start: the stored token is worthless, so drop it and end the identity provider session
    return this.leaveSession(`grant rejected: ${reason}`, { alsoAtStart: true });
  }

  /**
   * The session is over. At process start that is simply the `loggedOut` state — the route guards
   * keep the user on the login page. Later it is a full logout, which restarts the app, so nothing
   * of the old session survives in memory and the user lands on the login page fresh.
   */
  private leaveSession(reason: string, options: { alsoAtStart?: boolean } = {}): SessionState {
    if (this.state === 'starting' && !options.alsoAtStart) {
      return this.transition('loggedOut', reason);
    }
    this.transition('loggedOut', reason);
    // not awaited: the round trip to the identity provider must not hold up the caller
    void this.logout({ reason }).catch(error =>
      logSessionEvent(
        'logout_step_failed',
        'logout',
        `logout after the session ended failed: ${describeError(error)}`,
        error
      )
    );
    return 'loggedOut';
  }

  //#endregion

  //#region token

  /** Whether the loaded token still has its refresh margin left. */
  private get tokenUsable(): boolean {
    const token = this._oauth.currentToken;
    return token != null && isUsable(token);
  }

  /** The current token, refreshed first when it is about to expire (or when `force` is set). */
  private async ensureFreshToken(options: { force?: boolean } = {}): Promise<TokenCheck> {
    const current = this._oauth.currentToken;
    if (!current) {
      return this._oauth.tokenState === 'unknown' ? { kind: 'unreadable' } : { kind: 'none' };
    }
    if (!options.force && isUsable(current)) {
      return okToken(current);
    }
    if (!current.refreshToken) {
      return { kind: 'rejected', reason: 'access token expired and no refresh token available' };
    }
    return this.refreshOnce();
  }

  /** Single-flight: concurrent callers share one refresh, so a one-time-use refresh token is never spent twice. */
  private refreshOnce(): Promise<TokenCheck> {
    if (!this._refresh) {
      this._refresh = this.runRefresh().finally(() => {
        this._refresh = null;
      });
    }
    return this._refresh;
  }

  private async runRefresh(): Promise<TokenCheck> {
    logStep('refresh', `started | exp in ${this.describeTokenLifetime()}`);
    const result = await this._oauth.refresh();
    if (result.kind === 'refreshed') {
      const token = okToken(result.token);
      logStep('refresh', `ok | exp in ${formatRemaining(token.expiresAt)}`);
      return token;
    }
    logStep('refresh', `${result.kind} | ${result.reason}`);
    return result;
  }

  private describeTokenLifetime(): string {
    const token = this._oauth.currentToken;
    return token ? formatRemaining(expiresAtOf(token)) : 'no token';
  }

  /**
   * One timer for "the token needs refreshing": re-armed for every new token, cancelled on logout
   * and while the socket is down. `refreshDue` lets the resume handler catch up on a timer the
   * browser or the OS held back while the app was in the background.
   */
  private scheduleRefresh(refreshAtMs: number): void {
    this.cancelRefresh();
    const delay = Math.max(MIN_REFRESH_DELAY_MS, refreshAtMs - Date.now());
    this._refreshDueAt = Date.now() + delay;
    this._refreshHandle = setTimeout(
      () => {
        this._refreshHandle = null;
        void this.onRefreshDue();
      },
      Math.min(delay, MAX_TIMER_MS)
    );
  }

  private cancelRefresh(): void {
    if (this._refreshHandle != null) {
      clearTimeout(this._refreshHandle);
      this._refreshHandle = null;
    }
    this._refreshDueAt = null;
  }

  private get refreshDue(): boolean {
    return this._refreshDueAt != null && Date.now() >= this._refreshDueAt;
  }

  //#endregion

  //#region retries and transitions

  private scheduleRetry(): void {
    this.cancelRetry();
    const delay = RETRY_DELAYS_MS[Math.min(this._retryAttempt, RETRY_DELAYS_MS.length - 1)];
    this._retryAttempt++;
    logStep('retry', `attempt ${this._retryAttempt} in ${delay / 1000}s`);
    this._retryHandle = setTimeout(() => {
      this._retryHandle = null;
      void this.evaluate(`retry ${this._retryAttempt}`);
    }, delay);
  }

  private cancelRetry(): void {
    if (this._retryHandle != null) {
      clearTimeout(this._retryHandle);
      this._retryHandle = null;
    }
  }

  private resetRetries(): void {
    this.cancelRetry();
    this._retryAttempt = 0;
  }

  private transition(to: SessionState, reason: string): SessionState {
    const from = this.state;
    if (from === to) {
      logStep('state', `${to} (unchanged) | ${reason}`);
      return to;
    }
    logTransition(from, to, reason);
    this._state$.next(to);
    return to;
  }

  //#endregion
}

/** Whether the token still has its refresh margin left. */
function isUsable(token: ITokenSet): boolean {
  return Date.now() < token.expiresAt - refreshBufferFor(token);
}

function okToken(token: ITokenSet): TokenOk {
  const expiresAt = expiresAtOf(token);
  return { kind: 'ok', accessToken: token.accessToken, expiresAt, refreshAt: expiresAt - refreshBufferFor(token) };
}

/** EXPIRY_BUFFER_MS, capped at half of the token's lifetime so short tokens are renewed halfway, not at once. */
export function refreshBufferFor(token: ITokenSet): number {
  return Math.min(EXPIRY_BUFFER_MS, lifetimeOf(token) / 2);
}

function lifetimeOf(token: ITokenSet): number {
  return token.expiresIn * 1000;
}

export function expiresAtOf(token: ITokenSet): number {
  return token.expiresAt;
}

/** Web: a service worker registered for push must not outlive the session. */
async function unregisterServiceWorkers(): Promise<void> {
  try {
    const registrations = (await navigator.serviceWorker?.getRegistrations()) ?? [];
    await Promise.all(registrations.map(registration => registration.unregister()));
  } catch (error) {
    logSessionEvent(
      'logout_step_failed',
      'service_worker',
      `unregistering the service workers failed: ${describeError(error)}`,
      error
    );
  }
}

function describeCheck(check: TokenCheck): string {
  switch (check.kind) {
    case 'ok':
      return 'ok';
    case 'none':
      return 'no token';
    case 'unreadable':
      return 'token storage did not answer';
    default:
      return check.reason;
  }
}
